import type { WalletHorizon, WalletTier } from "../api/types";

/**
 * Показ квалификации кошелька-контрагента (веха M6).
 *
 * Чистый модуль без React: логика живёт в lib/*.ts, потому что vitest в этом
 * проекте включает только `*.test.ts` и компонентных тестов нет вовсе.
 */

/**
 * Ярлык тира по-русски.
 *
 * «неизвестен» и «мелкий» разведены намеренно и по всему интерфейсу: первое
 * означает «мы ещё не смотрели», второе — «посмотрели, и это мелочь». Слитые в
 * одно слово, они уверенно называли бы мелким кошелёк, историю которого не
 * успели выгрузить.
 */
const LABELS: Record<WalletTier, string> = {
  unknown: "неизвестен",
  noise: "мелкий",
  retail: "розница",
  sharp: "крупный",
  maker: "мейкер",
};

/**
 * Цвет тира.
 *
 * Токены из index.css, а не свои значения: палитра терминала одна, и второй
 * набор цветов разъехался бы с первым при любой правке темы.
 *
 * sharp красный не потому, что «плохо», а потому, что это единственный тир,
 * требующий действия: против крупного агрессора стоять в стакане опаснее всего.
 * mейкер, наоборот, нейтрален — с ним торгуют весь день.
 */
const COLORS: Record<WalletTier, string> = {
  unknown: "var(--color-muted)",
  noise: "var(--color-muted)",
  retail: "var(--color-fg)",
  sharp: "var(--color-sell)",
  maker: "var(--color-accent)",
};

export function tierLabel(tier: WalletTier | undefined): string {
  return tier ? LABELS[tier] : LABELS.unknown;
}

export function tierColor(tier: WalletTier | undefined): string {
  return tier ? COLORS[tier] : COLORS.unknown;
}

/**
 * Порядок тиров от самого «громкого» к самому тихому.
 *
 * Нужен там, где у сделки несколько контрагентов: в строке ленты помещается
 * один значок, и показывать надо САМОГО заметного, а не первого в массиве.
 * Порядок массива задаёт биржа, и он ничего не значит.
 */
const RANK: Record<WalletTier, number> = {
  sharp: 4,
  maker: 3,
  retail: 2,
  noise: 1,
  unknown: 0,
};

/**
 * Самый заметный тир среди контрагентов сделки.
 *
 * Возвращает undefined, если ни про одного контрагента ничего не известно —
 * это НЕ то же самое, что "unknown": «тир не сообщён» и «тир посчитан, и он
 * unknown» — разные утверждения (docs/bus-contracts.md).
 */
export function dominantTier(
  addresses: readonly string[] | undefined,
  tiers: Readonly<Record<string, WalletTier>>,
): { address: string; tier: WalletTier } | undefined {
  if (!addresses || addresses.length === 0) return undefined;

  let best: { address: string; tier: WalletTier } | undefined;
  for (const address of addresses) {
    const tier = tiers[address];
    if (!tier) continue;
    if (!best || RANK[tier] > RANK[best.tier]) best = { address, tier };
  }
  return best;
}

/** Ярлык горизонта для таблицы на странице кошелька. */
const HORIZONS: Record<WalletHorizon, string> = {
  "1h": "час",
  "24h": "сутки",
  all: "всё время",
};

export function horizonLabel(horizon: WalletHorizon): string {
  return HORIZONS[horizon] ?? horizon;
}

/**
 * Доля агрессии в процентах для показа.
 *
 * Приходит строкой с шестью знаками ("0.150000"), а на экране нужен процент.
 * Number здесь допустим и только здесь: это ПОКАЗ, а не расчёт, — величина
 * никогда не превращается в размер ордера. Правило «не делайте Number(price)»
 * защищает деньги, и доля под него не подпадает.
 */
export function aggressionPercent(ratio: string | undefined): string {
  if (!ratio) return "—";
  const value = Number(ratio);
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

/**
 * С какого отставания горизонт считается СНИМКОМ другого момента, а не «тем же
 * числом чуть постарше». Минута: горизонты пересчитываются раз в десять минут, и
 * секундная разница между строками одного прохода ничего не значит.
 */
const SNAPSHOT_LAG_MS = 60_000;

/**
 * Отстал ли горизонт от остальных строк таблицы — и на сколько.
 *
 * # Зачем
 *
 * Горизонт «всё время» пересчитывается ВЫГРУЗКОЙ, а она заканчивается один раз:
 * после `backfill.done` строка больше не обновляется, и на экране остаётся снимок
 * того момента. Замер 2026-08-12 на живом кошельке: 3114 сделок и оборот 178 685
 * в строке «всё время» против фактических 3714 и 229 135 у самого индексатора —
 * то есть таблица расходилась с биржей на треть, и по ней это было никак не
 * видно, ведь час и сутки рядом свежие.
 *
 * Возвращает миллисекунды отставания от самой свежей строки; ноль означает «это
 * тот же момент, что и у остальных».
 */
export function horizonLagMs(
  row: { updated_at?: string },
  rows: readonly { updated_at?: string }[],
): number {
  const at = Date.parse(row.updated_at ?? "");
  if (Number.isNaN(at)) return 0;
  let newest = at;
  for (const other of rows) {
    const t = Date.parse(other.updated_at ?? "");
    if (!Number.isNaN(t) && t > newest) newest = t;
  }
  const lag = newest - at;
  return lag >= SNAPSHOT_LAG_MS ? lag : 0;
}
